import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import PageHeader from '../../components/common/PageHeader';
import { FaChartLine, FaMobile, FaShieldAlt, FaClock, FaUsers, FaGlobe } from 'react-icons/fa';

const SecuritiesTradingBrokeragePage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const breadcrumbs = [
    { label: 'Services', link: '/services' },
    { label: 'Securities Trading & Brokerage' }
  ];

  const tradingServices = [
    {
      icon: <FaChartLine className="w-8 h-8 text-primary" />,
      title: "Equity Trading",
      description: "Comprehensive stock trading services with access to global equity markets and real-time execution."
    },
    {
      icon: <FaGlobe className="w-8 h-8 text-primary" />,
      title: "Fixed Income Securities",
      description: "Professional trading in government bonds, corporate bonds, and other fixed income instruments."
    },
    {
      icon: <FaMobile className="w-8 h-8 text-primary" />,
      title: "Options & Derivatives",
      description: "Advanced trading strategies using options, futures, and other derivative instruments."
    },
    {
      icon: <FaShieldAlt className="w-8 h-8 text-primary" />,
      title: "Foreign Exchange",
      description: "Currency trading services with access to major and minor currency pairs worldwide."
    },
    {
      icon: <FaClock className="w-8 h-8 text-primary" />,
      title: "Commodities Trading",
      description: "Direct access to commodity markets including precious metals, energy, and agricultural products."
    },
    {
      icon: <FaUsers className="w-8 h-8 text-primary" />,
      title: "Portfolio Execution",
      description: "Professional execution services for institutional portfolios and high-net-worth clients."
    }
  ];

  const platformFeatures = [
    "Advanced trading platforms with real-time market data",
    "Institutional-grade execution with best price guarantee",
    "24/7 market access across global exchanges",
    "Comprehensive research and market analysis tools",
    "Risk management and position monitoring systems",
    "Direct market access (DMA) for institutional clients",
    "Multi-asset trading from a single platform",
    "Dedicated relationship managers and trading support"
  ];

  const tradingSteps = [
    {
      step: "01",
      title: "Account Setup",
      description: "Complete account registration with full KYC compliance and trading authorization."
    },
    {
      step: "02",
      title: "Platform Access",
      description: "Receive login credentials and training on our advanced trading platforms."
    },
    {
      step: "03",
      title: "Market Research",
      description: "Access comprehensive market research, analysis, and investment recommendations."
    },
    {
      step: "04",
      title: "Execute Trades",
      description: "Begin trading with professional support and continuous portfolio monitoring."
    }
  ];

  return (
    <>
      <PageHeader 
        title="Securities Trading & Brokerage" 
        breadcrumbs={breadcrumbs}
        backgroundImage="/assets/trader-using-smartphone-stock-market-report-buy-sell-forex-trading_220873-8114.jpg_m0uqkt.jpg"
      />
      
      <section className="py-16 md:py-24 bg-background">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-heading font-bold text-primary mb-6">
                Professional Trading Solutions
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                <span>Segway Capital</span> provides comprehensive securities trading and brokerage 
                services with access to global financial markets. Our advanced trading platforms, competitive 
                pricing, and professional execution services cater to both individual investors and 
                institutional clients.
              </p>
              <p className="text-lg text-gray-700">
                With cutting-edge technology, deep market liquidity, and expert trading support, we deliver 
                the tools and services you need to execute your investment strategies efficiently and effectively.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
            >
              <img 
                src="/assets/traders-discussing-digital-currency-focus-real-time-market-graph-sellable_31965-465579.jpg_swhfj0.jpg" 
                alt="Securities Trading" 
                className="rounded-xl shadow-custom w-full h-full object-cover"
              />
            </motion.div>
          </div>

          {/* Trading Services */}
          <div className="mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl font-heading font-bold text-primary mb-4">Trading Services</h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Comprehensive trading solutions across multiple asset classes with professional execution 
                and institutional-grade technology.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {tradingServices.map((service, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-xl p-6 shadow-custom hover:shadow-custom-lg transition-all duration-300"
                >
                  <div className="bg-gray-100 p-4 rounded-full w-16 h-16 flex items-center justify-center mb-6">
                    {service.icon}
                  </div>
                  <h3 className="text-xl font-heading font-semibold mb-4 text-primary">{service.title}</h3>
                  <p className="text-gray-600">{service.description}</p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Trading Process */}
          <div className="mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl font-heading font-bold text-primary mb-4">Getting Started</h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                A simple four-step process to begin trading with professional support and advanced technology.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {tradingSteps.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="text-center"
                >
                  <div className="bg-gray-900 text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mb-6 mx-auto">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-heading font-semibold mb-4 text-primary">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Platform Features */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-heading font-bold text-primary mb-6">Platform Advantages</h2>
              <div className="space-y-4">
                {platformFeatures.map((feature, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-start"
                  >
                                    <div className="bg-gray-100 rounded-full p-2 mr-4 mt-1">
                  <div className="w-2 h-2 bg-gray-700 rounded-full"></div>
                    </div>
                    <p className="text-gray-700 text-lg">{feature}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
            >
              <img 
                src="/assets/hand-trading-forex-online-tablet-with-business-paper-coffee_1048944-27887900.jpg_x2g93v.jpg" 
                alt="Trading Platform" 
                className="rounded-xl shadow-custom w-full h-full object-cover"
              />
            </motion.div>
          </div>

          {/* Regulatory Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="bg-gray-50 rounded-2xl p-8 md:p-12 mb-16"
          >
            <div className="text-center">
              <h2 className="text-3xl font-heading font-bold text-primary mb-6">Regulated & Secure</h2>
              <p className="text-lg text-gray-700 mb-6 max-w-4xl mx-auto">
                All trading activities are conducted under strict regulatory oversight with full compliance 
                to international financial regulations. Your investments are protected by segregated client 
                accounts and comprehensive insurance coverage.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                <div>
                  <h3 className="text-xl font-semibold text-primary mb-2">CIMA Regulated</h3>
                  <p className="text-gray-600">Licensed by Cayman Islands Monetary Authority</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-primary mb-2">Client Protection</h3>
                  <p className="text-gray-600">Segregated accounts and investor compensation</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-primary mb-2">Secure Technology</h3>
                  <p className="text-gray-600">Bank-grade security and encryption protocols</p>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="bg-primary text-white rounded-2xl p-8 md:p-12"
          >
            <div className="text-center">
              <h2 className="text-3xl font-heading font-bold mb-6">Ready to Start Trading?</h2>
              <p className="text-xl text-white/90 mb-8 max-w-3xl mx-auto">
                Join thousands of satisfied clients who trust Segway Capital for their securities trading 
                and brokerage needs. Experience professional execution and superior service.
              </p>
              <a 
                href="/contact-us" 
                className="btn btn-secondary text-lg py-4 px-8 shadow-xl hover:shadow-secondary/20 btn-3d inline-block"
              >
                Open Trading Account
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default SecuritiesTradingBrokeragePage; 